import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { HealerTrinketAnalysisData, HealerTrinketBatchAnalysisData } from '../types';
import TbcErrorBanner from '../tbcAnalyzer/components/TbcErrorBanner';
import TbcFightBreakdownTable from '../tbcAnalyzer/components/TbcFightBreakdownTable';
import HealerTrinketComparisonGrid from '../tbcAnalyzer/components/HealerTrinketComparisonGrid';
import TbcLogUrlPhase from '../tbcAnalyzer/components/TbcLogUrlPhase';
import TbcPhaseHeader from '../tbcAnalyzer/components/TbcPhaseHeader';
import TbcSelectionPhase from '../tbcAnalyzer/components/TbcSelectionPhase';
import { buildHealerTrinketFightColumns } from '../tbcAnalyzer/healerTrinketTableColumns';
import { healerWasPresentInFight } from '../tbcAnalyzer/healerTrinketAggregates';
import { useTbcAnalyzerSession } from '../tbcAnalyzer/useTbcAnalyzerSession';
import { TbcReportRouteProps, useTbcReportRoute } from '../tbcAnalyzer/useTbcReportRoute';
import { fightFilterLabel, formatDuration, formatNumber } from '../tbcAnalyzer/utils';
import HelpTip from './HelpTip';

const LEGACY_LOG_URLS_KEY = 'wow_calcs.healerTrinketAnalyzer.logUrls';
const ANALYZE_TIMEOUT_MS = 10 * 60 * 1000;

const HealerTrinketAnalyzer: React.FC<TbcReportRouteProps> = ({ basePath, reportId }) => {
    const session = useTbcAnalyzerSession({ legacyLogUrlsKey: LEGACY_LOG_URLS_KEY });
    const [selectedPlayerNames, setSelectedPlayerNames] = useState<string[]>([]);
    const [result, setResult] = useState<HealerTrinketBatchAnalysisData | null>(null);
    const resultsRef = useRef<HTMLDivElement>(null);
    const reportRoute = useTbcReportRoute<HealerTrinketBatchAnalysisData, string[]>({
        basePath,
        reportId,
        session,
        onRestore: (restored, restoredPlayerNames) => {
            setResult(restored);
            setSelectedPlayerNames(restoredPlayerNames ?? []);
        },
        onLeave: () => {
            setResult(null);
        },
    });

    useEffect(() => {
        if (session.players.length === 0) {
            return;
        }
        const validNames = new Set(session.players.map((player) => player.name));
        setSelectedPlayerNames((current) => {
            const kept = current.filter((name) => validNames.has(name));
            if (kept.length > 0) {
                return kept;
            }
            return [session.players[0].name];
        });
    }, [session.players]);

    const handleTogglePlayerName = (name: string) => {
        setSelectedPlayerNames((current) => {
            if (current.indexOf(name) !== -1) {
                return current.filter((entry) => entry !== name);
            }
            return [...current, name];
        });
    };

    const handleSelectAllPlayers = () => {
        setSelectedPlayerNames(session.players.map((player) => player.name));
    };

    const handleClearPlayers = () => {
        setSelectedPlayerNames([]);
    };

    const handleProcessAnalysis = async () => {
        if (selectedPlayerNames.length === 0) {
            session.setError('Please select at least one healer for analysis');
            return;
        }
        if (session.loadedLogUrls.length === 0) {
            session.setError('No logs loaded');
            return;
        }
        if (session.bosses.length > 0 && session.selectedEncounterIds.length === 0) {
            session.setError('Please select at least one boss');
            return;
        }

        session.setLoading(true);
        session.setError(null);
        setResult(null);
        const wclProgressId = session.beginWclProgress();

        try {
            const orderedNames = session.players
                .map((player) => player.name)
                .filter((name) => selectedPlayerNames.indexOf(name) !== -1);
            const response = await axios.post(
                '/api/attendance/analyze-healer-trinkets',
                {
                    ...session.sharedRequestBody,
                    selectedPlayerNames: orderedNames,
                    wclProgressId,
                },
                { timeout: ANALYZE_TIMEOUT_MS }
            );
            setResult(response.data);
            session.setPhase('results');
            reportRoute.publish(response.data, selectedPlayerNames);
        } catch (err: any) {
            console.error('Error analyzing healer trinkets:', err);
            session.setError(
                err.response?.data?.error || err.response?.data?.details || 'Failed to analyze logs. Please try again.'
            );
        } finally {
            session.endWclProgress();
            session.setLoading(false);
        }
    };

    const handleBackToUrl = () => {
        session.handleBackToUrl();
        setResult(null);
        setSelectedPlayerNames([]);
    };

    const healers = result?.healers ?? [];
    const singleResult = healers.length === 1 ? healers[0] : null;
    const comparisonHealers = healers.length >= 2 ? healers : null;

    const filterLabel = result ? fightFilterLabel(result.fightFilter, session.bosses, result.fightFilterLabel) : '';

    const presentFights = useMemo(
        () => (singleResult ? singleResult.fights.filter(healerWasPresentInFight) : []),
        [singleResult]
    );

    const fightColumns = useMemo(
        () => (singleResult ? buildHealerTrinketFightColumns(singleResult) : []),
        [singleResult]
    );

    const analyzeButtonLabel =
        selectedPlayerNames.length <= 1
            ? 'Analyze Trinkets'
            : `Analyze Trinkets (${selectedPlayerNames.length} healers)`;

    const trinketHelp = (
        <HelpTip label="How healer trinket MP5 works" wide>
            <h4>What this shows</h4>
            <p>Mana those trinkets are worth, as MP5, from the logs you loaded:</p>
            <ul>
                <li>Darkmoon Card: Blue Dragon</li>
                <li>Eye of Gruul</li>
                <li>Earring of Soulful Meditation (priests)</li>
            </ul>
            <p>
                <strong>Theoretical</strong> is what the trinket would have been worth if it was equipped.
                <strong> Actual</strong> is what it did on fights where it was equipped.
            </p>
            <p>Pick one healer for a fight-by-fight report, or several to compare them side by side.</p>
        </HelpTip>
    );

    return (
        <div className="item-set-calculator">
            {session.phase !== 'results' && (
                <>
                    <h2 className="heading-with-help">
                        Healer Trinket MP5
                        {trinketHelp}
                    </h2>
                    <p className="analyzer-description">
                        Blue Dragon, Eye of Gruul, and (for priests) Earring of Soulful Meditation as MP5.
                    </p>
                </>
            )}

            <TbcErrorBanner error={session.error} />

            {session.phase === 'url' && !reportRoute.restoring && (
                <TbcLogUrlPhase
                    logUrlsText={session.logUrlsText}
                    loading={session.loading}
                    onLogUrlsChange={session.setLogUrlsText}
                    onLoadLogs={() => {
                        void session.handleLoadLogs();
                    }}
                    wclSnapshot={session.wclSnapshot}
                />
            )}

            {session.phase === 'selection' && (
                <TbcSelectionPhase
                    loadedLogUrls={session.loadedLogUrls}
                    players={session.players}
                    bosses={session.bosses}
                    selectedPlayerNames={selectedPlayerNames}
                    fightFilter={session.fightFilter}
                    selectedEncounterIds={session.selectedEncounterIds}
                    minDurationSeconds={session.minDurationSeconds}
                    loading={session.loading}
                    analyzeButtonLabel={analyzeButtonLabel}
                    showMinDurationFilter
                    minDurationHelpText="Fights shorter than this are left out."
                    onTogglePlayerName={handleTogglePlayerName}
                    onSelectAllPlayers={handleSelectAllPlayers}
                    onClearPlayers={handleClearPlayers}
                    onFightFilterChange={session.setFightFilter}
                    onToggleEncounterId={session.toggleEncounterId}
                    onToggleAllEncounterIds={session.toggleAllEncounterIds}
                    onToggleRaidEncounterIds={session.toggleRaidEncounterIds}
                    onMinDurationChange={session.setMinDurationSeconds}
                    onAnalyze={() => {
                        void handleProcessAnalysis();
                    }}
                    onBack={handleBackToUrl}
                    wclSnapshot={session.wclSnapshot}
                />
            )}

            {session.phase === 'results' && result && singleResult && (
                <div className="results-phase">
                    <TbcPhaseHeader
                        exportTarget={resultsRef}
                        title="Trinket MP5"
                        backLabel="← Back to Selection"
                        onBack={reportRoute.leave}
                        help={trinketHelp}
                    />

                    <p className="results-status">
                        {singleResult.selectedPlayer
                            ? `${singleResult.selectedPlayer.name} (${singleResult.selectedPlayer.class})`
                            : 'Unknown healer'}
                        {filterLabel ? ` · ${filterLabel}` : ''}
                        {singleResult.avgCpm !== null ? ` · ${formatNumber(singleResult.avgCpm, 1)} CPM` : ''}
                        {singleResult.duplicatesRemoved > 0
                            ? ` · −${singleResult.duplicatesRemoved} duplicate pull${
                                  singleResult.duplicatesRemoved === 1 ? '' : 's'
                              }`
                            : ''}
                    </p>

                    <TrinketSummaryCards
                        result={singleResult}
                        formatNumber={formatNumber}
                        formatDuration={formatDuration}
                    />

                    <div ref={resultsRef}>
                        <TbcFightBreakdownTable
                            fights={presentFights}
                            columns={fightColumns}
                            rowKey={(fight) => `${fight.reportId}-${fight.fightId}`}
                        />
                    </div>
                </div>
            )}

            {session.phase === 'results' && result && comparisonHealers && (
                <div className="results-phase">
                    <TbcPhaseHeader
                        exportTarget={resultsRef}
                        title="Trinket MP5"
                        backLabel="← Back to Selection"
                        onBack={reportRoute.leave}
                        help={trinketHelp}
                    />

                    <p className="results-status">
                        {comparisonHealers.length} healers
                        {filterLabel ? ` · ${filterLabel}` : ''}
                        {result.missingPlayerNames.length > 0
                            ? ` · missing ${result.missingPlayerNames.join(', ')}`
                            : ''}
                    </p>

                    <div ref={resultsRef}>
                        <HealerTrinketComparisonGrid healers={comparisonHealers} />
                    </div>
                </div>
            )}
        </div>
    );
};

interface TrinketSummaryCardsProps {
    result: HealerTrinketAnalysisData;
    formatNumber: (num: number, decimals?: number) => string;
    formatDuration: (seconds: number) => string;
}

const TrinketSummaryCards: React.FC<TrinketSummaryCardsProps> = ({ result, formatNumber, formatDuration }) => (
    <>
        <div className="trinket-mp5-row">
            <div className="mp5-card">
                <div className="mp5-header">
                    <h3>Darkmoon Card: Blue Dragon</h3>
                    <div className="mp5-value">
                        <span className="mp5-number">
                            {formatNumber(
                                result.blueDragon.equippedFights > 0
                                    ? result.blueDragon.mp5
                                    : result.blueDragon.theoreticalMp5,
                                1
                            )}
                        </span>
                        <span className="mp5-label">
                            {result.blueDragon.equippedFights > 0 ? 'MP5 equipped' : 'MP5 theoretical'}
                        </span>
                    </div>
                </div>
                <div className="mp5-subtitle">
                    {result.blueDragon.equippedFights > 0 && (
                        <>
                            {formatNumber(result.blueDragon.totalMana, 0)} extra mana · {result.blueDragon.totalProcs}{' '}
                            procs · {result.blueDragon.equippedFights}/{result.fightsProcessed} fights ·{' '}
                        </>
                    )}
                    theoretical {formatNumber(result.blueDragon.theoreticalMp5, 1)} MP5
                </div>
            </div>

            <div className="mp5-card">
                <div className="mp5-header">
                    <h3>Eye of Gruul</h3>
                    <div className="mp5-value">
                        <span className="mp5-number">
                            {formatNumber(
                                result.eyeOfGruul.equippedFights > 0
                                    ? result.eyeOfGruul.mp5
                                    : result.eyeOfGruul.theoreticalMp5,
                                1
                            )}
                        </span>
                        <span className="mp5-label">
                            {result.eyeOfGruul.equippedFights > 0 ? 'MP5 equipped' : 'MP5 theoretical'}
                        </span>
                    </div>
                </div>
                <div className="mp5-subtitle">
                    {result.eyeOfGruul.equippedFights > 0 && (
                        <>
                            {formatNumber(result.eyeOfGruul.totalMana, 0)} mana saved · {result.eyeOfGruul.consumed}/
                            {result.eyeOfGruul.totalProcs} procs used · {result.eyeOfGruul.equippedFights}/
                            {result.fightsProcessed} fights ·{' '}
                        </>
                    )}
                    theoretical {formatNumber(result.eyeOfGruul.theoreticalMp5, 1)} MP5
                </div>
            </div>

            {result.earring.applicable && (
                <div className="mp5-card">
                    <div className="mp5-header">
                        <h3>Earring of Soulful Meditation</h3>
                        <div className="mp5-value">
                            <span className="mp5-number">
                                {formatNumber(
                                    result.earring.equippedFights > 0
                                        ? result.earring.mp5
                                        : result.earring.theoreticalMp5,
                                    1
                                )}
                            </span>
                            <span className="mp5-label">
                                {result.earring.equippedFights > 0 ? 'MP5 equipped' : 'MP5 theoretical'}
                            </span>
                        </div>
                    </div>
                    <div className="mp5-subtitle">
                        {result.earring.equippedFights > 0 && (
                            <>
                                {formatNumber(result.earring.totalMana, 0)} extra mana · {result.earring.totalUses} use
                                {result.earring.totalUses === 1 ? '' : 's'} · {result.earring.equippedFights}/
                                {result.fightsProcessed} fights ·{' '}
                            </>
                        )}
                        theoretical {formatNumber(result.earring.theoreticalMp5, 1)} MP5
                    </div>
                </div>
            )}
        </div>

        <div className="results-summary">
            <div className="summary-card">
                <h4>Sample</h4>
                <div className="summary-stats">
                    <div className="stat">
                        <span className="stat-label">Fights processed:</span>
                        <span className="stat-value">{result.fightsProcessed}</span>
                    </div>
                    <div className="stat">
                        <span className="stat-label">Logs:</span>
                        <span className="stat-value">{result.reportIds.length}</span>
                    </div>
                    <div className="stat">
                        <span className="stat-label">Blue Dragon duration:</span>
                        <span className="stat-value">{formatDuration(result.blueDragon.durationSeconds)}</span>
                    </div>
                    <div className="stat">
                        <span className="stat-label">Eye of Gruul duration:</span>
                        <span className="stat-value">{formatDuration(result.eyeOfGruul.durationSeconds)}</span>
                    </div>
                    {result.earring.applicable && (
                        <div className="stat">
                            <span className="stat-label">Earring duration:</span>
                            <span className="stat-value">{formatDuration(result.earring.durationSeconds)}</span>
                        </div>
                    )}
                    <div className="stat">
                        <span className="stat-label">Avg spellcast CPM:</span>
                        <span className="stat-value">
                            {result.avgCpm !== null ? formatNumber(result.avgCpm, 1) : '—'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </>
);

export default HealerTrinketAnalyzer;
